import React, { lazy } from 'react';
import { motion } from 'framer-motion';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
interface ReasonSectionProps {
  number: number;
  objection: string;
  title: string;
  children: React.ReactNode;
}
function ReasonSection({
  number,
  objection,
  title,
  children
}: ReasonSectionProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6
  }} className="mt-9" data-id="element-9">
      <div className="mb-2" data-id="element-10">
        <span className="font-sans-editorial inline-flex items-center justify-center bg-[#2f7d5b] text-white text-[13px] font-extrabold tracking-[1px] w-[34px] h-[34px] rounded-full mb-[11px]" data-id="element-11">
          {number}
        </span>
        <h2 className="font-serif-editorial text-[24px] md:text-[26px] font-bold leading-[1.28] text-[#1a1a17] tracking-tight mb-4" data-id="element-12">
          {title}
        </h2>
      </div>
      <div className="space-y-[18px] text-[#3d3d38]" data-id="element-13">{children}</div>
    </motion.div>;
}
interface ReviewProps {
  quote: string;
  name: string;
}
function Review({
  quote,
  name
}: ReviewProps) {
  return <div className="bg-[#faf8f4] border border-[#e6e2d9] rounded-[10px] p-4 md:p-[18px] mb-[14px]" data-id="element-14">
      <div className="text-[#e8a93b] text-[15px] tracking-[2px] mb-2" data-id="element-15">
        ★★★★★
      </div>
      <div className="italic text-[16.5px] text-[#1a1a17] leading-[1.55] mb-[9px]" data-id="element-16">
        "{quote}"
      </div>
      <div className="font-sans-editorial text-[13px] font-bold text-[#6b6b62]" data-id="element-17">
        {name}
      </div>
    </div>;
}
interface PlantProps {
  name: string;
  description: React.ReactNode;
  imageSrc?: string;
  isKey?: boolean;
  titleColor?: string;
}
function Plant({
  name,
  description,
  imageSrc,
  isKey,
  titleColor = '#1a1a17'
}: PlantProps) {
  if (imageSrc) {
    return <div className="text-left" data-id="element-18">
        <img src={imageSrc} alt={name} className="w-[104px] h-[104px] object-contain block mb-2 mix-blend-multiply" data-id="element-19" />
        {isKey && <span className="inline-block font-sans-editorial text-[10px] font-extrabold tracking-[0.5px] uppercase text-white bg-[#2f7d5b] px-2 py-0.5 rounded-full mb-1.5" data-id="element-20">
            La planta clave
          </span>}
        <div className="font-sans-editorial font-extrabold text-[16px] mb-1 leading-[1.2]" style={{
        color: titleColor
      }} data-id="element-21">
          {name}
        </div>
        <div className="text-[14.5px] leading-[1.45] text-[#3d3d38]" data-id="element-22">
          {description}
        </div>
      </div>;
  }
  return <div className="mb-3 last:mb-0 text-[16px] leading-[1.5]" data-id="element-23">
      <span className="text-[17px] mr-1" data-id="element-24">🌿</span>
      <b className="text-[#0f3f46]" data-id="element-25">{name}</b> — {description}
    </div>;
}
function DesktopSidebar() {
  const distribution = [{
    stars: 5,
    pct: 92
  }, {
    stars: 4,
    pct: 68
  }, {
    stars: 3,
    pct: 32
  }, {
    stars: 2,
    pct: 14
  }, {
    stars: 1,
    pct: 6
  }];
  return <aside className="hidden md:block w-[300px] flex-shrink-0 md:sticky md:top-6 self-start" data-id="element-26">
      <div className="bg-white border border-[#e6e2d9] rounded-xl p-5 shadow-[0_2px_18px_rgba(0,0,0,0.05)]" data-id="element-27">
        <div className="font-sans-editorial font-extrabold text-[16px] text-[#1a1a17] mb-3 pb-3 border-b border-[#e6e2d9]" data-id="element-28">
          Reseñas de clientes
        </div>
        <div className="bg-[#faf8f4] border border-[#e6e2d9] rounded-lg py-4 text-center mb-4" data-id="element-29">
          <div className="font-sans-editorial font-extrabold text-[40px] text-[#1a1a17] leading-none" data-id="element-30">
            4,9
          </div>
          <div className="font-sans-editorial text-[12px] text-[#6b6b62] mt-1" data-id="element-31">
            43.247 reseñas
          </div>
        </div>
        <div className="space-y-[7px]" data-id="element-32">
          {distribution.map(({
          stars,
          pct
        }) => <div key={stars} className="flex items-center gap-2" data-id="element-33">
              <div className="text-[#e8a93b] text-[12px] tracking-[1px] w-[60px]" data-id="element-34">
                {'★'.repeat(stars)}
                <span className="text-[#e0dcd2]" data-id="element-35">{'★'.repeat(5 - stars)}</span>
              </div>
              <div className="flex-1 h-[7px] rounded-full bg-[#eeebe2] overflow-hidden" data-id="element-36">
                <div className="h-full bg-[#1f9d55] rounded-full" style={{
              width: `${pct}%`
            }} data-id="element-37" />
              </div>
            </div>)}
        </div>
      </div>

      <div className="bg-white border border-[#e6e2d9] rounded-xl p-5 mt-5 shadow-[0_2px_18px_rgba(0,0,0,0.05)] text-center" data-id="element-38">
        <div className="rounded-lg p-4 mb-4 relative overflow-hidden" style={{
        background: 'linear-gradient(160deg, #195A63, #0f3f46)'
      }} data-id="element-39">
          <img src="https://cdn.magicpatterns.com/uploads/6B7zQEFxHcdku6Fx1NZG7N/GOCHOA_(6).png" alt="Drenaje Linfático Sonvital" className="h-[140px] w-auto mx-auto drop-shadow-[0_8px_20px_rgba(0,0,0,0.35)]" data-id="element-40" />
          <div className="absolute top-3 right-3 bg-[#F5A623] text-[#3a2600] font-sans-editorial font-extrabold text-[11px] px-2 py-[3px] rounded-full" data-id="element-41">
            −50%
          </div>
        </div>
        <a href="https://sonvital.com/products/nueva-formula" target="_blank" rel="noopener noreferrer" className="font-sans-editorial font-extrabold text-[15px] text-[#1a4a7a] underline underline-offset-2 hover:text-[#0f3f46] block mb-2" data-id="element-42">
          Perder volumen de verdad hoy
        </a>
        <div className="font-sans-editorial text-[13px] text-[#3d3d38] leading-[1.45] mb-4" data-id="element-43">
          Haz clic abajo para comprobar disponibilidad y ahorrar hasta un 50%
        </div>
        <a href="https://sonvital.com/products/nueva-formula" target="_blank" rel="noopener noreferrer" className="block bg-[#1f9d55] text-white font-sans-editorial font-extrabold text-[14px] tracking-[0.5px] uppercase no-underline text-center py-[13px] rounded-lg hover:brightness-110 transition-all" data-id="element-44">
          👉 Comprobar Disponibilidad — 50% DESCUENTO
        </a>
      </div>
    </aside>;
}
export function AdvertorialPage() {
  return <div className="bg-[#faf8f4] min-h-screen pb-24" data-id="element-45">
      <div className="md:max-w-[960px] md:mx-auto md:flex md:gap-6 md:items-start md:pt-6 md:px-4" data-id="element-46">
        <div className="max-w-[600px] mx-auto bg-white min-h-screen shadow-[0_0_60px_rgba(0,0,0,0.04)] md:mx-0 md:flex-shrink-0 md:min-h-0 md:rounded-xl md:overflow-hidden md:w-[600px]" data-id="element-47">
          {/* Masthead */}
          <div className="border-b border-[#e6e2d9] px-[22px] py-[14px] flex items-center justify-between bg-white" data-id="element-48">
            <div className="flex items-center gap-[9px]" data-id="element-49">
              <div className="w-[30px] h-[30px] rounded-full bg-gradient-to-br from-[#2f8f7a] to-[#1f6e8c] flex items-center justify-center text-white font-sans-editorial font-extrabold text-[10px] tracking-[0.5px]" data-id="element-50">
                ABC
              </div>
              <div className="font-sans-editorial font-extrabold text-[17px] tracking-tight text-[#1a1a17]" data-id="element-51">
                Salud
                <span className="text-[#6b6b62] font-medium" data-id="element-52">yBienestar</span>
              </div>
            </div>
            <div className="font-sans-editorial text-[11px] font-bold tracking-[1.5px] uppercase text-[#2f7d5b]" data-id="element-53">
              Salud
            </div>
          </div>

          <article className="px-5 pt-[26px] text-[19.5px] leading-[1.6]" data-id="element-54">
            {/* Kicker */}
            <motion.div initial="hidden" animate="visible" variants={fadeIn} className="font-sans-editorial text-[12px] font-bold tracking-[1.2px] uppercase text-[#2f7d5b] mb-3" data-id="element-55">
              Informe · Salud y bienestar
            </motion.div>

            {/* Headline */}
            <motion.h1 initial="hidden" animate="visible" variants={fadeIn} transition={{
            delay: 0.05
          }} className="font-serif-editorial text-[32px] md:text-[36px] font-bold leading-[1.15] tracking-[-0.3px] text-[#1a1a17] mb-5" data-id="element-56">
              5 Razones Por Las Que Esta Potente Fórmula Natural Sí Elimina La
              Barriga Que Llevas Años Intentando Perder
            </motion.h1>

            {/* Hero GIF */}
            <figure className="mt-1.5 mb-[22px] rounded-xl overflow-hidden bg-[#eceae4] leading-none" data-id="element-57">
              <img src="https://cdn.shopify.com/s/files/1/1003/8303/1638/files/0617.gif?v=1781682492" alt="Mujer de más de 50 frustrada tras probar dietas sin resultados en la báscula" className="w-full h-auto block" loading="eager" fetchPriority="high" decoding="async" data-id="element-58" />
            </figure>

            {/* Body intro */}
            <div className="space-y-[18px] text-[#3d3d38]" data-id="element-59">
              <p className="text-[20px] text-[#1a1a17] font-medium" data-id="element-60">
                Probaste las dietas. Empezaste a hacer deporte. Incluso caíste
                en los masajes drenantes a 60 euros la sesión. Y siempre lo
                mismo: o no bajabas nada, o lo poco que perdías volvía en una
                semana. Normal que pienses:{' '}
                <em data-id="element-61">"¿y por qué iba a ser este distinto?"</em>
              </p>
              <p data-id="element-62">
                Es justo la pregunta que deberías hacerte. Pero aquí es donde
                todo cambia. Porque no es otra dieta ni otro masaje;{' '}
                <strong className="text-[#1a1a17]" data-id="element-63">SONVITAL</strong> es la
                primera fórmula que ataca lo que ninguna dieta ni tratamiento
                estético puede alcanzar.
              </p>
            </div>

            {/* Reason 1 */}
            <ReasonSection number={1} objection="" title="Ataca La Causa Oculta Que Las Dietas No Tocan" data-id="element-64">
              <figure className="my-2 rounded-xl overflow-hidden border border-[#e6e2d9] leading-none" data-id="element-65">
                <img src="https://cdn.magicpatterns.com/uploads/wkFDNQ3N43WEEbE5DQZGBT/ChatGPT_Image_17_jun_2026,_09_50_11.png" alt="Sistema linfático bloqueado vs desbloqueado: el líquido vuelve a fluir" className="w-full h-auto block" loading="lazy" data-id="element-66" />
              </figure>
              <p data-id="element-67">
                Imagina subirte a la báscula después de semanas cuidándote y que
                por fin el número baje de verdad. Eso es lo que pasa cuando el
                problema no era lo que comías, sino lo que tu cuerpo no podía
                sacar.
              </p>
              <p className="bg-[#faf8f4] border-l-4 border-l-[#2f7d5b] rounded-r-[8px] px-4 py-3 italic text-[17px] text-[#1a1a17] leading-[1.55]" data-id="element-68">
                Una endocrinóloga lo explicó así a una de sus pacientes: "Llevas
                años haciendo dieta, pero tu sistema linfático está bloqueado
                por dentro. Mientras esos bloqueos sigan ahí, da igual lo que
                comas o dejes de comer. El líquido se acumula, te infla, y la
                báscula no se mueve. No es falta de voluntad. Es que nadie te ha
                solucionado el problema de raíz."
              </p>
              <p data-id="element-69">
                Para eso se creó{' '}
                <strong className="text-[#1a1a17]" data-id="element-70">
                  Drenaje Linfático de Sonvital
                </strong>
                , y su secreto tiene nombre: la{' '}
                <strong className="text-[#1a1a17]" data-id="element-71">Raíz de Stillingia</strong>.
              </p>
              <p data-id="element-72">
                La mayoría de dietas y tratamientos intentan quemar grasa o
                mover el líquido desde fuera. Stillingia hace algo distinto: se
                orienta a los{' '}
                <strong className="text-[#1a1a17]" data-id="element-73">
                  bloqueos más profundos del sistema linfático
                </strong>
                , los que ninguna dieta, masaje ni maderoterapia alcanza. Es
                decir, abre la salida por donde el líquido retenido tiene que
                irse. Sin eso, todo lo demás es trabajo perdido.<sup data-id="element-74">*</sup>
              </p>
              <p data-id="element-75">
                Pero la Stillingia sola no basta, y por eso Sonvital la combina
                en una{' '}
                <strong className="text-[#1a1a17]" data-id="element-76">Fórmula 4 en 1</strong> de
                plantas que trabajan en secuencia:
              </p>
              <div className="bg-[#faf8f4] border border-[#e6e2d9] rounded-xl p-5 md:p-6 my-2 grid grid-cols-2 gap-x-[18px] gap-y-[26px]" data-id="element-77">
                <Plant name="Raíz de Stillingia" description="Abre los bloqueos linfáticos más profundos." imageSrc="https://cdn.magicpatterns.com/uploads/fZra5PbW8ZiVCdKT9Qs9gT/32.png" isKey={true} titleColor="#195A63" data-id="element-78" />
                <Plant name="Amor de Hortelano" description='La "escoba linfática": despierta los vasos dormidos.' imageSrc="https://cdn.magicpatterns.com/uploads/ni41kiZBnWm112jfoW4XMb/31.png" data-id="element-79" />
                <Plant name="Corteza de Fresno Espinoso" description="Reactiva la microcirculación periférica." imageSrc="https://cdn.magicpatterns.com/uploads/aBjBAhcgNZt6f1bK7PKtCe/30.png" data-id="element-80" />
                <Plant name="Trébol Rojo" description="Acompaña el equilibrio hormonal en la menopausia." imageSrc="https://cdn.magicpatterns.com/uploads/qrddJ32654E8C8yLccbrJA/29.png" data-id="element-81" />
              </div>
            </ReasonSection>

            {/* Reason 2 */}
            <ReasonSection number={2} objection="" title="Sin Dietas Extremas, Sin Efecto Rebote" data-id="element-82">
              <figure className="my-2 rounded-xl overflow-hidden border border-[#e6e2d9] leading-none" data-id="element-83">
                <img src="https://cdn.magicpatterns.com/uploads/mbnc2Y9jBhPV5usocJNBQr/ChatGPT_Image_17_jun_2026,_09_52_05.png" alt="Comparativa: dietas y masajes drenantes vs Drenaje Linfático Sonvital con plantas" className="w-full h-auto block" loading="lazy" data-id="element-84" />
              </figure>
              <p data-id="element-85">
                Si has hecho dietas restrictivas, ya sabes cómo funciona: pasas
                hambre, bajas dos kilos, lo dejas, y en diez días has recuperado
                tres. Con la maderoterapia igual: te moldean por fuera, pero por
                dentro el líquido sigue atascado. Pan para hoy, hambre para
                mañana.
              </p>
              <p data-id="element-86">
                <strong className="text-[#1a1a17]" data-id="element-87">
                  Sonvital no fuerza nada.
                </strong>{' '}
                Es 100% botánica y viene en{' '}
                <strong className="text-[#1a1a17]" data-id="element-88">extracto líquido</strong>, no
                en cápsula. Esa diferencia lo cambia todo: el líquido empieza a
                actuar ya en la boca, sin tener que esperar a que una pastilla
                se disuelva.
              </p>
              <p data-id="element-89">
                Sin efectos secundarios. Sin acostumbramiento. Sin pasar hambre.
                Pensada para tomar a diario apoyando el drenaje natural de tu
                cuerpo, no sustituyéndolo.<sup data-id="element-90">*</sup>
              </p>
            </ReasonSection>

            {/* Reason 3 */}
            <ReasonSection number={3} objection="" title="Resultados Que Se Ven En El Espejo, No Solo En La Báscula" data-id="element-91">
              <figure className="my-2 rounded-xl overflow-hidden border border-[#e6e2d9] leading-none" data-id="element-92">
                <img src="https://cdn.magicpatterns.com/uploads/cUHZNQfyFCStiAi4Ao1WMR/bec62ad79adaa550b0c801e956d74c4e_1_1780242773_6468.png" alt="Mujer mirándose al espejo con satisfacción tras reducir volumen" className="w-full h-auto block" loading="lazy" data-id="element-93" />
              </figure>
              <p data-id="element-94">
                Las dietas te prometen kilos. Los masajes te prometen
                centímetros. Pero ninguno soluciona ese aspecto hinchado, ese
                volumen que no tiene que ver con la grasa sino con todo el
                líquido que tu cuerpo no consigue eliminar.
              </p>
              <p data-id="element-95">
                Con <strong className="text-[#1a1a17]" data-id="element-96">Sonvital</strong> notas
                cómo el volumen va bajando de verdad, no solo por la mañana,
                sino durante todo el día. La barriga, las piernas, la cara. Te
                miras al espejo y empiezas a reconocerte. Y eso es lo que de
                verdad importa.<sup data-id="element-97">*</sup>
              </p>
            </ReasonSection>

            {/* Reason 4 — with embedded reviews */}
            <ReasonSection number={4} objection="¿Tengo alguna prueba de que esto funciona?" title="Más de 43.000 personas en España ya lo usan a diario" data-id="element-98">
              <figure className="my-2 rounded-xl overflow-hidden border border-[#e6e2d9] leading-none" data-id="element-99">
                <img src="https://cdn.magicpatterns.com/uploads/sc6bjcWTQwWoPag4VzGwWB/ChatGPT_Image_31_may_2026,_16_58_27.png" alt="Collage de personas en España que toman Drenaje Linfático de Sonvital" className="w-full h-auto block" loading="lazy" data-id="element-100" />
              </figure>
              <p data-id="element-101">
                No hace falta creer en promesas: basta con escuchar a quienes ya
                lo toman. Más de{' '}
                <strong className="text-[#1a1a17]" data-id="element-102">43.000 personas</strong> en
                España lo han incorporado a su rutina, y los comentarios se
                repiten tanto que cuesta ignorarlos.
              </p>
              <div className="my-[30px]" data-id="element-103">
                <Review quote="Llevo años a dieta y la báscula no se movía. En tres semanas con esto noté que la barriga estaba más plana. No era grasa, era líquido acumulado que mi cuerpo no sacaba." name="Carmen L., 52 — Barcelona" data-id="element-104" />
                <Review quote="Me gasté un dineral en maderoterapia y masajes. Funcionaban dos días y volvía a estar igual. Con Sonvital fue la primera vez que el resultado se mantuvo." name="Pilar G., 48 — Madrid" data-id="element-105" />
                <Review quote="Mi marido fue el primero en notarlo. Me dijo: 'Te veo la cara más fina.' Yo me miraba al espejo y por primera vez en años me reconocía." name="Rosa M., 55 — Sevilla" data-id="element-106" />
              </div>
            </ReasonSection>

            {/* Reason 5 */}
            <ReasonSection number={5} objection="¿Y si pago y a mí no me funciona?" title="60 días para probarlo, con devolución completa si no notas la diferencia" data-id="element-107">
              <figure className="my-2 rounded-xl overflow-hidden border border-[#e6e2d9] leading-none" data-id="element-108">
                <img src="https://cdn.magicpatterns.com/uploads/eBVLTnt5HEYLUuZcVqsRpt/ChatGPT_Image_31_may_2026,_17_09_59.png" alt="Garantía de 60 días sin riesgo con Drenaje Linfático Sonvital" className="w-full h-auto block" loading="lazy" data-id="element-109" />
              </figure>
              <p data-id="element-110">
                Cada pedido viene con una{' '}
                <strong className="text-[#1a1a17]" data-id="element-111">
                  garantía de devolución de 60 días
                </strong>
                . Si no notas la diferencia, devuelves el frasco —aunque esté
                vacío— y te devuelven el dinero. Sin preguntas.
              </p>
              <p data-id="element-112">
                Es la forma de que el riesgo lo asuma la marca, no tú: si no
                funcionara, no podrían permitirse ofrecer dos meses enteros de
                garantía.
              </p>
            </ReasonSection>

            {/* En resumen — versus box */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5
          }} className="bg-[#195A63] rounded-xl p-5 my-[22px] text-[#eaf3f3]" data-id="element-113">
              <div className="font-sans-editorial font-extrabold text-[13px] tracking-[1px] uppercase text-[#a9cdd0] mb-[14px] text-center" data-id="element-114">
                En resumen
              </div>
              <div className="flex gap-3 items-start mb-[11px] text-[15.5px] leading-[1.45]" data-id="element-115">
                <span className="text-[#ff9b9b] font-extrabold flex-shrink-0" data-id="element-116">
                  ✕
                </span>
                <div data-id="element-117">
                  Las dietas, los masajes y la maderoterapia atacan{' '}
                  <b className="text-white" data-id="element-118">desde fuera</b>, y el alivio dura lo
                  que dura el tratamiento
                </div>
              </div>
              <div className="flex gap-3 items-start text-[15.5px] leading-[1.45]" data-id="element-119">
                <span className="text-[#86e0a8] font-extrabold flex-shrink-0" data-id="element-120">
                  ✓
                </span>
                <div data-id="element-121">
                  Sonvital combina{' '}
                  <b className="text-white" data-id="element-122">4 plantas en secuencia</b> y va a la
                  causa, en <b className="text-white" data-id="element-123">extracto líquido</b> que
                  se absorbe mucho antes que una cápsula
                </div>
              </div>
            </motion.div>

            {/* CTA after summary */}
            <a href="https://sonvital.com/products/nueva-formula" target="_blank" rel="noopener noreferrer" className="cta-button block bg-[#1f9d55] text-white font-sans-editorial font-extrabold text-[18px] no-underline text-center px-[18px] py-[17px] rounded-xl tracking-[0.2px] leading-[1.3] my-[22px] hover:brightness-110" data-id="element-124">
              👉 Comprobar Disponibilidad — 50% DESCUENTO
            </a>

            {/* Alert — Señales de que tu sistema linfático necesita ayuda */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5
          }} className="bg-[#fdf3f1] border border-[#f0d4cc] border-l-4 border-l-[#9c2a2a] rounded-r-[10px] p-5 my-[30px]" data-id="element-125">
              <h3 className="font-sans-editorial font-extrabold text-[16px] text-[#9c2a2a] mb-[13px] leading-[1.35]" data-id="element-126">
                Señales de que tu sistema linfático necesita ayuda ya
              </h3>
              <ul className="list-none" data-id="element-127">
                {['Llevas semanas a dieta y la báscula no baja ni medio kilo', 'Te notas hinchada incluso cuando comes bien', 'La ropa te aprieta más por la tarde que por la mañana', 'Te miras al espejo y no te reconoces', 'Has probado de todo y estás a punto de tirar la toalla'].map((item, i) => <li key={i} className="relative pl-[26px] mb-[10px] last:mb-0 text-[16px] leading-[1.5] text-[#3d3d38]" data-id="element-128">
                    <span className="absolute left-0 top-0 text-[#9c2a2a] text-[14px]" data-id="element-129">
                      ⚠
                    </span>
                    {item}
                  </li>)}
              </ul>
              <p className="mt-[14px] mb-0 text-[16px] text-[#1a1a17] font-medium" data-id="element-130">
                Si te reconoces en dos o más, el problema no es tu fuerza de
                voluntad: es un bloqueo que tu cuerpo lleva arrastrando. Y cada
                mes que pasa cuesta un poco más revertirlo.
              </p>
            </motion.div>

            {/* Stock Bar */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.97
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} className="bg-[#fff6e8] border border-dashed border-[#F5A623] rounded-[10px] px-4 py-[13px] mt-6 mb-2 font-sans-editorial font-bold text-[14.5px] text-[#8a5a06] text-center leading-[1.5]" data-id="element-131">
              ⚠️ STOCK LIMITADO — Últimos packs disponibles ·{' '}
              <span className="text-[#9c2a2a]" data-id="element-132">
                50% DESCUENTO + envío gratis 24-48h
              </span>
            </motion.div>

            {/* Product Box */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5
          }} className="rounded-2xl p-6 md:p-[26px] mt-[18px] mb-[30px] text-center text-[#eaf3f3]" style={{
            background: 'linear-gradient(160deg, #195A63, #0f3f46)'
          }} data-id="element-133">
              <img src="https://cdn.magicpatterns.com/uploads/oVNo75UHQYBgocamkkjdPE/ELITE_UTILS_(10).png" alt="Sonvital" className="h-[34px] w-auto mx-auto mb-2" data-id="element-134" />
              <div className="font-sans-editorial text-[12.5px] tracking-[2px] uppercase text-[#a9cdd0] mb-[18px]" data-id="element-135">
                Drenaje Linfático · Fórmula 4 en 1
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/6B7zQEFxHcdku6Fx1NZG7N/GOCHOA_(6).png" alt="Bote de Drenaje Linfático Sonvital" className="h-[140px] w-auto mx-auto mb-3 drop-shadow-[0_10px_24px_rgba(0,0,0,0.35)]" data-id="element-136" />
              <div className="font-sans-editorial font-extrabold text-[26px] text-white mb-2" data-id="element-137">
                Oferta Limitada
              </div>
              <div className="inline-block bg-[#F5A623] text-[#3a2600] font-sans-editorial font-extrabold text-[13px] px-3 py-[5px] rounded-full mb-4" data-id="element-138">
                −50% HOY
              </div>
              <div className="font-sans-editorial text-[13.5px] text-[#cfe4e5] mb-5" data-id="element-139">
                Envío GRATIS en 24-48h a toda España · Garantía 60 días
              </div>
              <a href="https://sonvital.com/products/nueva-formula" target="_blank" rel="noopener noreferrer" className="cta-button block bg-[#1f9d55] text-white font-sans-editorial font-extrabold text-[18px] no-underline text-center px-[18px] py-[17px] rounded-xl tracking-[0.2px] leading-[1.3] hover:brightness-110" data-id="element-140">
                👉 Comprobar Disponibilidad
                <br data-id="element-141" />
                50% DESCUENTO
              </a>
            </motion.div>

            {/* Guarantee */}
            <div className="flex gap-[14px] items-center bg-[#faf8f4] border border-[#e6e2d9] rounded-xl p-4 md:px-[18px] md:py-4 my-[22px]" data-id="element-142">
              <div className="flex-shrink-0 w-[54px] h-[54px] rounded-full bg-[#1f9d55] text-white flex items-center justify-center font-sans-editorial font-extrabold text-[13px] text-center leading-[1.1]" data-id="element-143">
                60
                <br data-id="element-144" />
                DÍAS
              </div>
              <div className="text-[15px] leading-[1.5] text-[#3d3d38]" data-id="element-145">
                <b className="text-[#1a1a17]" data-id="element-146">
                  Garantía de devolución de 60 días.
                </b>{' '}
                Si no notas la diferencia, devuelves el frasco (aunque esté
                vacío) y te devuelven el dinero. Sin preguntas.
              </div>
            </div>

            <p className="text-center font-sans-editorial text-[14px] text-[#6b6b62] mt-[10px] mb-0" data-id="element-147">
              Más de <b className="text-[#1a1a17]" data-id="element-148">43.000 personas en España</b>{' '}
              ya lo han incorporado a su rutina diaria.
            </p>

            {/* Solo CTA */}
            <a href="https://sonvital.com/products/nueva-formula" target="_blank" rel="noopener noreferrer" className="cta-button block bg-[#1f9d55] text-white font-sans-editorial font-extrabold text-[18px] no-underline text-center px-[18px] py-[17px] rounded-xl tracking-[0.2px] leading-[1.3] my-[26px] hover:brightness-110" data-id="element-149">
              👉 Comprobar Disponibilidad — 50% DESCUENTO
            </a>
          </article>

          {/* Footer */}
          <footer className="border-t border-[#e6e2d9] mt-[34px] px-[22px] pt-6 pb-[120px] font-sans-editorial text-[11.5px] text-[#6b6b62] leading-[1.6]" data-id="element-150">
            <div className="mb-[14px]" data-id="element-151">
              <sup data-id="element-152">*</sup> Complemento alimenticio. No sustituye una dieta
              variada y equilibrada ni un estilo de vida saludable. No superar
              la dosis diaria recomendada. Mantener fuera del alcance de los
              niños. Si estás embarazada, en período de lactancia, tomas
              medicación o tienes alguna condición médica, consulta con tu
              profesional sanitario antes de tomarlo.
            </div>
            <div data-id="element-153">
              © 2026 Salud y Bienestar. Todos los derechos reservados. ·{' '}
              <a href="#" className="text-[#6b6b62] hover:underline" data-id="element-154">
                Privacidad
              </a>{' '}
              ·{' '}
              <a href="#" className="text-[#6b6b62] hover:underline" data-id="element-155">
                Términos
              </a>{' '}
              ·{' '}
              <a href="#" className="text-[#6b6b62] hover:underline" data-id="element-156">
                Contacto
              </a>
            </div>
            <div className="mt-[10px]" data-id="element-157">
              Este sitio no forma parte del sitio web de Facebook ni de Facebook
              Inc. Tampoco está respaldado por Facebook de ninguna manera.
              FACEBOOK es una marca registrada de Facebook, Inc.
            </div>
          </footer>
        </div>
        <DesktopSidebar data-id="element-158" />
      </div>
    </div>;
}